import React from 'react'
import { clsx } from 'clsx'

interface ChipProps {
    /**
     * The content to display inside the chip
     */
    children: React.ReactNode
    /**
     * Visual variant of the chip
     */
    variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'
    /**
     * Size of the chip
     */
    size?: 'sm' | 'md' | 'lg'
    /**
     * Optional icon to display before the text
     */
    icon?: React.ReactNode
    /**
     * Additional CSS classes
     */
    className?: string
    /**
     * Whether the chip is dismissible
     */
    dismissible?: boolean
    /**
     * Callback when chip is dismissed
     */
    onDismiss?: () => void
    /**
     * Additional click handler
     */
    onClick?: () => void
}

const Chip: React.FC<ChipProps> = ({
    children,
    variant = 'default',
    size = 'md',
    icon,
    className,
    dismissible = false,
    onDismiss,
    onClick,
}) => {
    const baseClasses = 'inline-flex items-center font-medium rounded-full transition-colors'

    const variantClasses = {
        default: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        success: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300',
        warning: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300',
        error: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300',
        info: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300',
        primary: 'bg-primary-100 dark:bg-primary-900/20 text-primary-800 dark:text-primary-300',
        secondary: 'bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300',
    }

    const sizeClasses = {
        sm: 'px-2 py-1 text-xs gap-1',
        md: 'px-3 py-1 text-sm gap-1.5',
        lg: 'px-4 py-2 text-base gap-2',
    }

    const iconSizes = {
        sm: 'w-3 h-3',
        md: 'w-4 h-4',
        lg: 'w-5 h-5',
    }

    const isClickable = onClick || dismissible

    const chipClasses = clsx(
        baseClasses,
        variantClasses[variant],
        sizeClasses[size],
        isClickable && 'cursor-pointer hover:opacity-80',
        className
    )

    const handleClick = () => {
        if (onClick) {
            onClick()
        }
    }

    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onDismiss) {
            onDismiss()
        }
    }

    return (
        <span
            className={chipClasses}
            onClick={handleClick}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    handleClick()
                }
            }}
        >
            {icon && (
                <span className={clsx(iconSizes[size], 'flex-shrink-0 flex items-center justify-center')}>
                    {icon}
                </span>
            )}
            <span className="flex-shrink-0">{children}</span>
            {dismissible && (
                <button
                    type="button"
                    className={clsx(
                        'ml-1 rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors',
                        iconSizes[size]
                    )}
                    onClick={handleDismiss}
                    aria-label="Remove"
                >
                    <svg
                        className="w-full h-full"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            )}
        </span>
    )
}

export default Chip